import React, { forwardRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { PHOTO_CONTROL_CLASSES } from './photoControls';

/**
 * Go back in the app's history, or to `fallback` when there is none.
 *
 * React Router (v6 BrowserRouter) stores the entry's position in
 * history.state.idx: 0 is the first entry of this tab, so navigate(-1) would
 * leave the site or do nothing. Read at click time. NOT
 * location.key !== 'default' — every replace (ProtectedRoute's sign-in
 * redirect, the first-run /welcome redirect) mints a new key without adding
 * an entry, so a fresh arrival through a redirect looked like it had history.
 *
 * @param {string|null} fallback - Route for a page opened directly (from a
 *   notification, a shared link or a fresh tab). Without one, navigate(-1).
 */
export function useGoBack(fallback = null) {
  const navigate = useNavigate();
  return useCallback(() => {
    const hasInAppHistory = (window.history.state?.idx ?? 0) > 0;
    if (fallback && !hasInAppHistory) navigate(fallback, { replace: true });
    else navigate(-1);
  }, [navigate, fallback]);
}

const VARIANT_CLASSES = {
  // On the page background, a sticky bar or a sheet header
  page: 'text-content-primary hover:bg-surface-tertiary',
  // Floating over a cover photo, matching the like/save/share circles beside it
  photo: '',
};

/**
 * BackButton — the one back control (Kurtis, 2026-10-02). A left chevron in a
 * 44px circle, labelled "Go back". Replaces the arrow/chevron/"‹ Back" text
 * variants that had grown across pages.
 *
 * Props:
 * @param {'page'|'photo'} variant - photo: the floating circle over a cover
 *   photo (its look comes from photoControls.js)
 * @param {string} fallback - Route used when there is no in-app history
 *   (see useGoBack). Ignored when onClick is passed.
 * @param {Function} onClick - Replaces navigation entirely: a previous step
 *   inside a sheet or modal, or a page with its own back logic.
 * @param {string} ariaLabel - Overrides "Go back" when the target is specific
 *   ("Back to settings list").
 * @param {string} className - Placement (margins, absolute positioning)
 * Anything else (data-*, title, aria-*) passes through; a ref reaches the
 * <button> (focus-on-open in dialogs).
 */
const BackButton = forwardRef(function BackButton({ variant = 'page', fallback = null, onClick = null, ariaLabel = null, className = '', ...rest }, ref) {
  const { t } = useTranslation('common');
  const goBack = useGoBack(fallback);
  const isPhoto = variant === 'photo';
  const classes = [
    'w-11 h-11 flex-shrink-0 inline-flex items-center justify-center rounded-full transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light',
    isPhoto ? PHOTO_CONTROL_CLASSES : VARIANT_CLASSES.page,
    className,
  ].filter(Boolean).join(' ');
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick || goBack}
      className={classes}
      aria-label={ariaLabel || t('pageTitle.backAriaLabel')}
      {...rest}
    >
      <Icon name="chevronLeft" className="w-5 h-5" />
    </button>
  );
});

export default BackButton;
